import { useParams, useNavigate, Navigate } from 'react-router-dom';
import MeditationPlayer from '@/components/meditation/meditation-player';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useContentWithConfig, useContentList } from '@/lib/hooks/use-content';
import Loading from '@/components/loading';
import { useProfile } from '@/lib/providers';
import { AdminRole } from '@/types/custom';
import { useToast } from '@/components/ui/use-toast';
import { canSeeBinauralBeats, ROUTES } from '@/lib/constants';

/**
 * Renders a single binaural beat for playing.
 * Similar to pages/meditation.tsx
 */
export default function BinauralBeatPage() {
  const { profile, loadingProfile } = useProfile();
  const isAdmin = profile?.role === AdminRole;

  const [isExpanded, setIsExpanded] = useState(false);
  const [tipsExpanded, setTipsExpanded] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { contentWithConfig: contentData, loading: loadingContent } =
    useContentWithConfig(id);
  const { contentWithConfigs: allBeats } = useContentList('binaural', []);

  const handleNext = () => {
    const published = allBeats.filter((b) => b.content.published || isAdmin);
    const currentIndex = published.findIndex((b) => b.content.id === id);
    if (published.length > 1) {
      const nextIndex = (currentIndex + 1) % published.length;
      navigate(`${ROUTES.BINAURAL_BEATS}/${published[nextIndex].content.id}`);
    }
  };

  if (loadingProfile || loadingContent) {
    return <Loading />;
  }

  // The section is not released yet, so a stale link or a typed URL goes home
  // rather than into a page nothing navigates to.
  if (!canSeeBinauralBeats(isAdmin)) {
    return <Navigate to="/" replace />;
  }

  if (!loadingContent && !contentData) {
    toast({
      title: 'Niet gevonden',
      description: `ID ${id}`,
    });
    navigate(ROUTES.NOT_FOUND, { replace: true });
    return;
  }

  const tipsContent = (
    <div className="space-y-4">
      <div
        className="bg-white/90 backdrop-blur-sm rounded-lg p-6 w-full cursor-pointer group"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            Algemene Tips
          </h2>
          <ChevronDown
            className={`h-5 w-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          />
        </div>
        {isExpanded && (
          <div className="mt-4 space-y-4">
            <div className="space-y-3 text-gray-700">
              <div>
                <h3 className="text-lg font-semibold mb-2">
                  Wat doet muziek met ons brein?
                </h3>
                <p className="mb-2">
                  Muziek bestaat kort door de bocht uit trillingen op
                  bepaalde frequenties (tonen) die in ons brein
                  terechtkomen. Deze trillingen activeren hersendelen,
                  stimuleren de aanmaak van neurotransmitters zoals GABA en
                  dopamine en roepen emoties op die horen bij onze
                  persoonlijke breinprogrammering.
                </p>
                <p className="mb-2">
                  Het luisteren naar de juiste muziek kan onder meer je
                  spieren ontspannen, hartslag en bloeddruk verlagen, pijn
                  verlichten en de aanmaak van stresshormonen zoals cortisol
                  verminderen.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">
                  Wat zijn binaural beats?
                </h3>
                <p className="mb-2">
                  Binaural beats gebruiken een speciale techniek waarbij
                  twee tonen op een iets verschillende frequentie
                  tegelijkertijd worden afgespeeld, in elk oor één. Je brein
                  compenseert dit door een derde toon te produceren die
                  overeenkomt met een specifieke hersengolf.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div
        className="bg-white/90 backdrop-blur-sm rounded-lg p-6 w-full cursor-pointer group"
        onClick={() => setTipsExpanded(!tipsExpanded)}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            Specifieke Tips bij deze Binaural Beat
          </h2>
          <ChevronDown
            className={`h-5 w-5 transition-transform ${tipsExpanded ? 'rotate-180' : ''}`}
          />
        </div>
        {tipsExpanded && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <h3 className="font-semibold text-gray-900">Bij Aanvang</h3>
              </div>
              <ul className="text-sm text-gray-700 space-y-1 list-disc pl-4">
                <li>Zorg dat je rust en vertrouwen uitstraalt</li>
                <li>Probeer een veilig contact op te bouwen</li>
                <li>Ga naar een plek waar je je veilig voelt</li>
                <li>Pas je tempo aan in spreken en handelen</li>
              </ul>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-orange-500" />
                <h3 className="font-semibold text-gray-900">Tijdens</h3>
              </div>
              <ul className="text-sm text-gray-700 space-y-1 list-disc pl-4">
                <li>Schat in of je dichterbij kan komen</li>
                <li>Kijk of aanraking van meerwaarde is</li>
                <li>Zorg voor zo min mogelijk afleidende prikkels</li>
                <li>Zorg voor bevestiging en geruststelling</li>
              </ul>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <h3 className="font-semibold text-gray-900">
                  Bij Afsluiting
                </h3>
              </div>
              <ul className="text-sm text-gray-700 space-y-1 list-disc pl-4">
                <li>Zorg voor bevestiging</li>
                <li>Geef complimenten</li>
                <li>Benadruk wat goed is gegaan</li>
                <li>Vraag naar hun beleving</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const fullscreenTips = (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-[900px]">
      <div className="space-y-3 bg-white/10 backdrop-blur-sm rounded-lg p-4">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <h3 className="font-semibold text-white">Bij Aanvang</h3>
        </div>
        <ul className="text-sm text-white/80 space-y-1 list-disc pl-4">
          <li>Zorg dat je rust en vertrouwen uitstraalt</li>
          <li>Probeer een veilig contact op te bouwen</li>
          <li>Ga naar een plek waar je je veilig voelt</li>
          <li>Pas je tempo aan in spreken en handelen</li>
        </ul>
      </div>

      <div className="space-y-3 bg-white/10 backdrop-blur-sm rounded-lg p-4">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full bg-orange-500" />
          <h3 className="font-semibold text-white">Tijdens</h3>
        </div>
        <ul className="text-sm text-white/80 space-y-1 list-disc pl-4">
          <li>Schat in of je dichterbij kan komen</li>
          <li>Kijk of aanraking van meerwaarde is</li>
          <li>Zorg voor zo min mogelijk afleidende prikkels</li>
          <li>Zorg voor bevestiging en geruststelling</li>
        </ul>
      </div>

      <div className="space-y-3 bg-white/10 backdrop-blur-sm rounded-lg p-4">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full bg-green-500" />
          <h3 className="font-semibold text-white">Bij Afsluiting</h3>
        </div>
        <ul className="text-sm text-white/80 space-y-1 list-disc pl-4">
          <li>Zorg voor bevestiging</li>
          <li>Geef complimenten</li>
          <li>Benadruk wat goed is gegaan</li>
          <li>Vraag naar hun beleving</li>
        </ul>
      </div>
    </div>
  );

  return (
    <MeditationPlayer
      contentData={contentData}
      onNext={handleNext}
      selectionContent={tipsContent}
      fullscreenContent={fullscreenTips}
    />
  );
}
